import * as d3 from "d3";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { VennDiagram, sortAreas } from "venn.js/build/venn.js";

import Utils from "../../QlikUtils";
import FilterMessage from "../FilterMessage/FilterMessage";
import ObjectError from "../ObjectError/ObjectError";
import ObjectLoader from "../ObjectLoader/ObjectLoader";

const Venn = ({ app, reference, style }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterNeeded, setFilterNeeded] = useState("");
  const chartRef = useRef(null);
  const refsToClose = useRef(null);

  const needFilter = (hypercube) => {
    if (hypercube?.qCalcCondMsg) {
      setFilterNeeded(hypercube.qCalcCondMsg);
      setLoading(false);
      return true;
    }
    setLoading(false);
    setFilterNeeded("");
    return false;
  };

  useEffect(() => {
    if (reference?.id) {
      Utils.createHyperCubeByQlikId(app, reference.id, (newData, vis) => {
        refsToClose.current = { hypercube: newData, vis };
        if (newData && newData.code) {
          setError({
            message: "Could not load object",
          });
          setLoading(false);
          return;
        }

        if (needFilter(newData.qHyperCube)) {
          return;
        }
        const matrix = newData.qHyperCube.qDataPages[0].qMatrix[0];
        const seriesData = [
          { sets: ["A"], size: 16, label: matrix[0].qText },
          { sets: ["A", "B"], size: 6, label: matrix[1].qText },
          { sets: ["B"], size: 16, label: matrix[2].qText },
        ];
        setData(seriesData);
        setLoading(false);
      });
    }

    return () => {
      if (refsToClose.current) {
        Utils.destroyHC(
          app,
          refsToClose.current?.hypercube,
          refsToClose.current?.vis
        );
        setLoading(true);
        setData([]);
        setFilterNeeded("");
      }
    };
  }, [reference.id]);

  function getCircles(item) {
    if (item[0] === "A" && item.length == 1) {
      return "PR sem OCS";
    }
    if (item[0] === "B") {
      return "OCS sem PR";
    }
    if (item.length > 1) {
      return "PR e OCS";
    }
  }

  // function getClick(item) {
  //   if (item[0] === "A" && item.length == 1) {
  //     app.variable.setStringValue("vExibeConjunto", "7");
  //     window.location.href = "#/basket-mix/report";
  //   }
  //   if (item[0] === "B") {
  //     app.variable.setStringValue("vExibeConjunto", "8");
  //     window.location.href = "#/basket-mix/report";
  //   }
  //   if (item.length > 1) {
  //     app.variable.setStringValue("vExibeConjunto", "9");
  //     window.location.href = "#/basket-mix/report";
  //   }
  // }

  useEffect(() => {
    if (data && data.length > 0 && chartRef.current) {
      const chart = VennDiagram().width(250).height(150);
      chart.orientationOrder((a, b) => a.setid.localeCompare(b.setid));
      d3.select(chartRef.current).datum(data).call(chart);

      const div = d3.select(chartRef.current);
      div.datum(data).call(chart);

      const tooltip = d3
        .select("#venn")
        .append("div")
        .attr("class", "venntooltip");

      div
        .selectAll("g")

        .on("mouseenter", function (d, i) {
          sortAreas(div, i);
          tooltip.transition().duration(400).style("opacity", 0.9);
          tooltip.html(
            `<div> <h2> ${getCircles(i.sets)} </h2> <span> ${
              i.label
            } </span></div>`
          );
          const selection = d3.select(this).transition("tooltip").duration(400);
          selection
            .select("path")
            .style("stroke-width", 3)
            .style("fill-opacity", i.sets.length == 1 ? 0.4 : 0.1)
            .style("stroke-opacity", 1);
        })

        .on("click", function (event, i) {
          // getClick(i.sets);
        })

        .on("mousemove", function (event, i) {
          const [x, y] = d3.pointer(event);
          tooltip.style("left", x + 150 + "px").style("top", y + 150 + "px");
        })

        .on("mouseleave", function (d, i) {
          tooltip.transition().duration(400).style("opacity", 0);
          const selection = d3.select(this).transition("tooltip").duration(400);
          selection
            .select("path")
            .style("fill-opacity", i.sets.length == 1 ? 0.25 : 0.0)
            .style("stroke-opacity", 0);
        });

      const vennDiv = document.getElementById("venn");
      const vennSvg = vennDiv.children[0];

      vennDiv.setAttribute("class", "svg-container content-height");
      vennSvg.removeAttribute("height");
      vennSvg.removeAttribute("width");
      vennSvg.setAttribute("viewBox", "0 0 250 150");
      vennSvg.setAttribute("preserveAspectRatio", "xMaxYMin meet");
      vennSvg.setAttribute("class", "svg-content-responsive");

      setLoading(false);
    }
  }, [data, chartRef]);

  if (loading) {
    return <ObjectLoader icon="circle" width="inherit" height="inherit" />;
  }

  return (
    <div style={{ ...style }}>
      {error ? (
        <ObjectError message={error.message} />
      ) : filterNeeded !== "" ? (
        <FilterMessage message={filterNeeded} />
      ) : (
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
          id="venn"
          className="venn"
          ref={chartRef}
        />
      )}
    </div>
  );
};

Venn.defaultProps = {
  app: null,
  reference: {},
  style: {},
};
Venn.propTypes = {
  app: PropTypes.object,
  reference: PropTypes.object,
  style: PropTypes.object,
};

export default Venn;
